// Inline style-placement editor used inside the product_preview block on
// the Page Designer canvas. Renders the master's base mockup, overlays
// every style-attribute element (collar / cuff / pocket / placket /
// buttons / …), and lets the operator drag each one to reposition or
// drag a corner handle to resize. Save writes back to
// ecommerce-images/{masterId}/master.json via the existing
// /api/style-master/{id} endpoints, which also bust the runtime cache
// so the customer-facing preview picks up the new placement on the next
// composite render.
//
// Why live in the block (not the standalone designer page)? Operators
// kept asking why placement editing wasn't *inside* the preview they
// were configuring — the standalone page felt detached from the
// product-spec context, and they couldn't see how a placement change
// would land in the smart-page they were authoring. Embedding the
// editor in the block closes that loop.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  loadStyleMaster,
  saveStyleMaster,
  bootstrapStyleMaster,
  StyleMaster,
  AnchorDef,
} from '@/api/styleMaster'

type DragMode = 'move' | 'resize-br' | 'resize-tr' | 'resize-bl' | 'resize-tl'

interface DragState {
  name: string
  mode: DragMode
  startMouseX: number
  startMouseY: number
  startAnchorX: number
  startAnchorY: number
  startScale: number
}

const ANCHOR_COLOURS: Record<string, string> = {
  collar:  '#dc2626',
  cuff:    '#0ea5e9',
  pocket:  '#16a34a',
  placket: '#a855f7',
  buttons: '#f59e0b',
}

const FALLBACK_ATTRS: Record<string, string> = {
  fabric: 'cotton',
  collar: 'spread',
  cuff: 'single',
  pocket: 'patch',
  placket: 'standard',
  buttons: 'white',
}


function colourFor(attr: string): string {
  if (ANCHOR_COLOURS[attr]) return ANCHOR_COLOURS[attr]
  let h = 0
  for (let i = 0; i < attr.length; i++) h = (h * 31 + attr.charCodeAt(i)) >>> 0
  return `hsl(${h % 360}, 65%, 45%)`
}


function defaultAttrsFromMaster(m: StyleMaster): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [attr, opts] of Object.entries(m.elements || {})) {
    const keys = Object.keys(opts)
    out[attr] = FALLBACK_ATTRS[attr] && keys.includes(FALLBACK_ATTRS[attr])
      ? FALLBACK_ATTRS[attr]
      : (keys[0] || '')
  }
  return out
}


function elementImageUrl(
  masterId: string,
  master: StyleMaster,
  attr: string,
  value: string,
): string | null {
  const rel = master.elements?.[attr]?.[value]
  if (!rel) return null
  if (rel.startsWith('../')) return `/ecommerce-images/${rel.slice(3)}`
  return `/ecommerce-images/${masterId}/${rel}`
}


function anchorOffset(name: string | undefined, w: number, h: number): [number, number] {
  switch ((name || 'center').toLowerCase()) {
    case 'center': return [w / 2, h / 2]
    case 'top-center': return [w / 2, 0]
    case 'bottom-center': return [w / 2, h]
    case 'top-left': return [0, 0]
    case 'top-right': return [w, 0]
    case 'bottom-left': return [0, h]
    case 'bottom-right': return [w, h]
    default: return [w / 2, h / 2]
  }
}


export function ProductPreviewPlacementEditor({
  masterId,
  onMasterIdChange,
}: {
  masterId: string
  // The block stores `placementMasterId`; if the operator types a new
  // id into the inline picker we forward it back to the block so the
  // selection persists across saves.
  onMasterIdChange?: (next: string) => void
}) {
  const [master, setMaster] = useState<StyleMaster | null>(null)
  const [savedMaster, setSavedMaster] = useState<StyleMaster | null>(null)
  const [missing, setMissing] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [bootstrapping, setBootstrapping] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [attrs, setAttrs] = useState<Record<string, string>>({})
  const [imgDims, setImgDims] = useState<Record<string, { w: number; h: number }>>({})
  const [hint, setHint] = useState<'idle' | 'edited'>('idle')
  // Local picker for the master id when blank.
  const [draftId, setDraftId] = useState(masterId)
  const svgRef = useRef<SVGSVGElement | null>(null)
  const dragRef = useRef<DragState | null>(null)

  const dirty = useMemo(
    () => !!master && !!savedMaster && JSON.stringify(master) !== JSON.stringify(savedMaster),
    [master, savedMaster],
  )

  useEffect(() => {
    if (!masterId) return
    let cancelled = false
    setMissing(false)
    setErrorMsg(null)
    setMaster(null)
    setSavedMaster(null)
    loadStyleMaster(masterId)
      .then((m) => {
        if (cancelled) return
        setMaster(m)
        setSavedMaster(JSON.parse(JSON.stringify(m)))
        setAttrs(defaultAttrsFromMaster(m))
        setSelected(Object.keys(m.anchors || {}).filter((k) => !k.startsWith('_'))[0] || null)
      })
      .catch((err) => {
        if (cancelled) return
        const status = err?.response?.status
        if (status === 404) {
          setMissing(true)
          return
        }
        setErrorMsg(err?.response?.data?.detail || err?.message || 'Failed to load master')
      })
    return () => {
      cancelled = true
    }
  }, [masterId])

  const updateAnchor = useCallback((name: string, patch: Partial<AnchorDef>) => {
    setMaster((m) => {
      if (!m) return m
      return {
        ...m,
        anchors: {
          ...m.anchors,
          [name]: { ...m.anchors[name], ...patch },
        },
      }
    })
    setHint('edited')
  }, [])

  const clientToSvg = useCallback((cx: number, cy: number): [number, number] => {
    const svg = svgRef.current
    if (!svg) return [0, 0]
    const pt = svg.createSVGPoint()
    pt.x = cx
    pt.y = cy
    const ctm = svg.getScreenCTM()
    if (!ctm) return [0, 0]
    const out = pt.matrixTransform(ctm.inverse())
    return [out.x, out.y]
  }, [])

  const onElementPointerDown = useCallback(
    (name: string, mode: DragMode, e: React.PointerEvent<SVGElement>) => {
      if (!master) return
      const a = master.anchors[name]
      if (!a) return
      e.preventDefault()
      e.stopPropagation()
      try { (e.target as SVGElement).setPointerCapture(e.pointerId) } catch { /* not all elements support capture */ }
      const [sx, sy] = clientToSvg(e.clientX, e.clientY)
      dragRef.current = {
        name,
        mode,
        startMouseX: sx,
        startMouseY: sy,
        startAnchorX: a.x,
        startAnchorY: a.y,
        startScale: a.scale ?? 1.0,
      }
      setSelected(name)
    },
    [master, clientToSvg],
  )

  const onPointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    const drag = dragRef.current
    if (!drag) return
    const [sx, sy] = clientToSvg(e.clientX, e.clientY)
    if (drag.mode === 'move') {
      const nx = Math.round(drag.startAnchorX + (sx - drag.startMouseX))
      const ny = Math.round(drag.startAnchorY + (sy - drag.startMouseY))
      updateAnchor(drag.name, { x: nx, y: ny })
      return
    }
    // Resize: scale the start-distance to current-distance ratio. The
    // anchor coordinate is the pivot, which keeps the element pinned to
    // its placement point during the resize.
    const dx = sx - drag.startAnchorX
    const dy = sy - drag.startAnchorY
    const startDx = drag.startMouseX - drag.startAnchorX
    const startDy = drag.startMouseY - drag.startAnchorY
    const startMag = Math.hypot(startDx, startDy)
    const nowMag = Math.hypot(dx, dy)
    if (startMag < 1) return
    const ratio = nowMag / startMag
    const next = Math.max(0.05, Math.min(8, drag.startScale * ratio))
    updateAnchor(drag.name, { scale: Number(next.toFixed(3)) })
  }, [clientToSvg, updateAnchor])

  const onPointerUp = useCallback(() => { dragRef.current = null }, [])

  const onSave = useCallback(async () => {
    if (!master) return
    setSaving(true)
    try {
      await saveStyleMaster(masterId, master)
      setSavedMaster(JSON.parse(JSON.stringify(master)))
      setHint('idle')
      toast.success('Placement saved')
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string }
      toast.error(String(e?.response?.data?.detail || e?.message || 'Save failed'))
    } finally {
      setSaving(false)
    }
  }, [master, masterId])

  const onReset = useCallback(() => {
    if (!savedMaster) return
    setMaster(JSON.parse(JSON.stringify(savedMaster)))
    setHint('idle')
  }, [savedMaster])

  const onBootstrap = useCallback(async () => {
    if (!masterId) return
    setBootstrapping(true)
    try {
      const m = await bootstrapStyleMaster(masterId, 'shirt_master')
      setMaster(m)
      setSavedMaster(JSON.parse(JSON.stringify(m)))
      setAttrs(defaultAttrsFromMaster(m))
      setSelected(Object.keys(m.anchors || {})[0] || null)
      setMissing(false)
      toast.success(`Created '${masterId}' from shirt_master`)
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string }
      toast.error(String(e?.response?.data?.detail || e?.message || 'Bootstrap failed'))
    } finally {
      setBootstrapping(false)
    }
  }, [masterId])

  // ── Rendering ─────────────────────────────────────────────────────

  // Empty / picker state — operator hasn't named a master yet.
  if (!masterId) {
    return (
      <PlacementShell title="Style placement editor">
        <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-secondary)' }}>
          Pick a style master to edit. Use a product id (e.g. <code>prod_001</code>)
          or a category id; if the folder doesn't exist yet you'll be offered a
          one-click clone of <code>shirt_master</code>.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
          <input
            value={draftId}
            onChange={(e) => setDraftId(e.target.value.replace(/[^A-Za-z0-9_\-]/g, ''))}
            placeholder="prod_001"
            style={INPUT}
          />
          <button
            onClick={() => onMasterIdChange?.(draftId)}
            disabled={!draftId}
            style={primaryBtn(!!draftId)}
          >
            Load
          </button>
        </div>
      </PlacementShell>
    )
  }

  if (errorMsg) {
    return (
      <PlacementShell title="Style placement editor">
        <p style={{ color: 'var(--color-error, #dc2626)', margin: 0 }}>Error: {errorMsg}</p>
      </PlacementShell>
    )
  }

  if (missing) {
    return (
      <PlacementShell title="Style placement editor">
        <p style={{ margin: 0, fontSize: 14 }}>
          No master at <code>ecommerce-images/{masterId}/master.json</code>.
          Clone from <code>shirt_master</code> to get a working baseline you can retune.
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button onClick={onBootstrap} disabled={bootstrapping} style={primaryBtn(!bootstrapping)}>
            {bootstrapping ? 'Creating…' : `Create '${masterId}' from shirt_master`}
          </button>
          <button onClick={() => onMasterIdChange?.('')} style={secondaryBtn}>Pick different id</button>
        </div>
      </PlacementShell>
    )
  }

  if (!master) {
    return (
      <PlacementShell title="Style placement editor">
        <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-secondary)' }}>Loading <code>{masterId}</code>…</p>
      </PlacementShell>
    )
  }

  const cw = master.canvas.width
  const ch = master.canvas.height
  const basePath = master.base.file.startsWith('../')
    ? `/ecommerce-images/${master.base.file.slice(3)}`
    : `/ecommerce-images/${masterId}/${master.base.file}`
  const anchorEntries = Object.entries(master.anchors).filter(([k]) => !k.startsWith('_'))

  return (
    <PlacementShell title="Style placement editor">
      {/* Header strip: master id, dirty hint, Save / Reset, change id. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Master:</span>
        <code style={{ fontSize: 13 }}>{masterId}</code>
        <button onClick={() => onMasterIdChange?.('')} style={ghostBtn} title="Pick a different master">
          change…
        </button>
        <span style={{ flex: 1 }} />
        {hint === 'edited' && !dirty && (
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            Tip: drag images on the preview to reposition; drag corner squares to resize.
          </span>
        )}
        {dirty && (
          <span style={{ fontSize: 13, color: '#b45309' }}>Unsaved changes</span>
        )}
        <button onClick={onReset} disabled={!dirty} style={secondaryBtn}>Reset</button>
        <button onClick={onSave} disabled={!dirty || saving} style={primaryBtn(dirty && !saving)}>
          {saving ? 'Saving…' : 'Save placement'}
        </button>
      </div>

      {/* Attr-value picker — lets the operator preview a different
          collar / cuff / pocket etc. while tuning placement, since each
          option PNG can be a different shape. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {Object.keys(master.elements || {}).map((attr) => {
          const opts = Object.keys(master.elements[attr] || {})
          return (
            <label key={attr} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
              <span style={{ color: 'var(--color-text-secondary)' }}>{attr}:</span>
              <select
                value={attrs[attr] || opts[0] || ''}
                onChange={(e) => setAttrs({ ...attrs, [attr]: e.target.value })}
                style={smallInput}
              >
                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
          )
        })}
      </div>

      {/* Canvas: SVG with intrinsic viewBox of master canvas, scaled to
          fit the block width. Height capped so the block still fits a
          page-designer viewport. */}
      <div style={{
        background: '#f3f4f6',
        borderRadius: 8,
        overflow: 'hidden',
        maxHeight: 540,
        display: 'flex',
        justifyContent: 'center',
      }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${cw} ${ch}`}
          style={{
            width: '100%',
            height: 'auto',
            maxHeight: 540,
            display: 'block',
            // touch-action none so dragging on touch devices doesn't
            // scroll the page underneath.
            touchAction: 'none',
          }}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <image href={basePath} x={0} y={0} width={cw} height={ch} />

          {anchorEntries.map(([name, def]) => {
            const value = attrs[name] || ''
            const url = elementImageUrl(masterId, master, name, value)
            const dims = imgDims[name]
            const scale = def.scale ?? 1.0
            const w = dims ? dims.w * scale : 200
            const h = dims ? dims.h * scale : 200
            const [ox, oy] = anchorOffset(def.anchor, w, h)
            const x = def.x - ox
            const y = def.y - oy
            const isSelected = selected === name
            const colour = colourFor(name)
            return (
              <g key={name}>
                {url && (
                  <image
                    href={url}
                    x={x}
                    y={y}
                    width={w}
                    height={h}
                    style={{ cursor: 'grab', touchAction: 'none' }}
                    onPointerDown={(e) => onElementPointerDown(name, 'move', e)}
                    onClick={() => setSelected(name)}
                    onLoad={(e) => {
                      const img = e.currentTarget as unknown as { naturalWidth?: number; naturalHeight?: number }
                      const nw = img.naturalWidth || w
                      const nh = img.naturalHeight || h
                      setImgDims((prev) => {
                        const cur = prev[name]
                        if (cur && cur.w === nw && cur.h === nh) return prev
                        return { ...prev, [name]: { w: nw, h: nh } }
                      })
                    }}
                  />
                )}

                {isSelected && (
                  <>
                    <rect
                      x={x}
                      y={y}
                      width={w}
                      height={h}
                      fill="none"
                      stroke={colour}
                      strokeWidth={3}
                      strokeDasharray="8 6"
                      pointerEvents="none"
                    />
                    {([
                      ['resize-tl', x, y],
                      ['resize-tr', x + w, y],
                      ['resize-bl', x, y + h],
                      ['resize-br', x + w, y + h],
                    ] as Array<[DragMode, number, number]>).map(([mode, hx, hy]) => (
                      <rect
                        key={mode}
                        x={hx - 14}
                        y={hy - 14}
                        width={28}
                        height={28}
                        fill="white"
                        stroke={colour}
                        strokeWidth={3}
                        rx={4}
                        ry={4}
                        style={{ cursor: 'nwse-resize', touchAction: 'none' }}
                        onPointerDown={(e) => onElementPointerDown(name, mode, e)}
                      />
                    ))}
                  </>
                )}

                {/* Anchor dot + label — keeps a clear grab target even
                    when an element image is huge or has transparent
                    edges that don't catch pointer events. */}
                <g
                  transform={`translate(${def.x}, ${def.y})`}
                  style={{ cursor: 'grab', touchAction: 'none' }}
                  onPointerDown={(e) => onElementPointerDown(name, 'move', e)}
                  onClick={() => setSelected(name)}
                >
                  <circle r={isSelected ? 16 : 10} fill={colour} opacity={0.9} stroke="white" strokeWidth={3} />
                  <text
                    x={22}
                    y={6}
                    style={{
                      fontFamily: 'system-ui, sans-serif',
                      fontSize: 23,
                      fontWeight: 600,
                      fill: '#111827',
                      paintOrder: 'stroke',
                      stroke: 'white',
                      strokeWidth: 4,
                    }}
                    pointerEvents="none"
                  >
                    {name}
                  </text>
                </g>
              </g>
            )
          })}
        </svg>
      </div>

      {/* Sidecar: numeric editor for the selected anchor. Useful for
          fine-tuning by 1px after a coarse drag. */}
      {selected && master.anchors[selected] && (
        <div style={{
          display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
          padding: '8px 10px', borderRadius: 6,
          background: 'var(--pg-surface-alt, rgba(0,0,0,0.04))',
        }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{selected}</span>
          <NumLabel label="x" value={master.anchors[selected].x}
            onChange={(v) => updateAnchor(selected, { x: v })} />
          <NumLabel label="y" value={master.anchors[selected].y}
            onChange={(v) => updateAnchor(selected, { y: v })} />
          <NumLabel label="scale" step={0.05}
            value={master.anchors[selected].scale ?? 1.0}
            onChange={(v) => updateAnchor(selected, { scale: v })} />
          <label style={{ fontSize: 13 }}>
            anchor:{' '}
            <select
              value={master.anchors[selected].anchor || 'center'}
              onChange={(e) => updateAnchor(selected, { anchor: e.target.value })}
              style={smallInput}
            >
              {['center','top-center','bottom-center','top-left','top-right','bottom-left','bottom-right'].map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </label>
        </div>
      )}
    </PlacementShell>
  )
}


// ── Layout / button primitives ─────────────────────────────────────

function PlacementShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      // Don't let drag handles bubble into the page-designer's own
      // block-drag system — clicking inside the editor must select
      // shapes, not move the whole block.
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{
        display: 'flex', flexDirection: 'column', gap: 10, padding: 14,
        borderRadius: 10,
        border: '1px dashed var(--color-border, rgba(127,127,127,0.4))',
        background: 'var(--pg-surface-alt, rgba(76,141,255,0.04))',
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          fontSize: 19, lineHeight: 1, padding: '6px 8px', borderRadius: 8,
          background: 'var(--color-accent, #4c8dff)', color: '#fff',
        }}>🪞</span>
        <span style={{ fontWeight: 600, fontSize: '1.0125rem' }}>{title}</span>
        <span style={{
          marginLeft: 'auto', fontSize: 13, fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.06em',
          color: 'var(--color-text-secondary)',
          padding: '2px 6px', border: '1px solid var(--color-border)', borderRadius: 999,
        }}>eCommerce</span>
      </div>
      {children}
    </div>
  )
}


function NumLabel({
  label, value, onChange, step = 1,
}: { label: string; value: number; onChange: (v: number) => void; step?: number }) {
  return (
    <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ color: 'var(--color-text-secondary)' }}>{label}</span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        step={step}
        onChange={(e) => {
          const v = parseFloat(e.target.value)
          if (Number.isFinite(v)) onChange(v)
        }}
        style={{ ...smallInput, width: 80 }}
      />
    </label>
  )
}


const INPUT: React.CSSProperties = {
  flex: 1,
  padding: '6px 8px',
  borderRadius: 6,
  border: '1px solid var(--color-border, #d1d5db)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: 14,
}

const smallInput: React.CSSProperties = {
  padding: '3px 6px',
  borderRadius: 4,
  border: '1px solid var(--color-border, #d1d5db)',
  background: 'var(--color-input-bg)',
  color: 'var(--color-text)',
  fontSize: 13,
}

function primaryBtn(active: boolean): React.CSSProperties {
  return {
    padding: '6px 12px',
    border: 'none',
    borderRadius: 6,
    background: active ? '#0ea5e9' : '#d1d5db',
    color: 'white',
    cursor: active ? 'pointer' : 'not-allowed',
    fontSize: 13,
    fontWeight: 600,
  }
}

const secondaryBtn: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--color-border, #d1d5db)',
  borderRadius: 6,
  background: 'transparent',
  color: 'var(--color-text)',
  cursor: 'pointer',
  fontSize: 13,
}

const ghostBtn: React.CSSProperties = {
  padding: '2px 6px',
  border: 'none',
  borderRadius: 4,
  background: 'transparent',
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
  fontSize: 13,
  textDecoration: 'underline',
}
